"use client"

import { useState } from "react"
import "./LawsSystem.css"

const LawsSystem = ({ gameState, setGameState, addNotification }) => {
  const [newLaw, setNewLaw] = useState({ title: "", description: "" })
  const [isCreating, setIsCreating] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState("governance")

  const lawCategories = {
    governance: { name: "Governance", icon: "🏛️", color: "#4ecdc4" },
    economy: { name: "Economy", icon: "💰", color: "#feca57" },
    environment: { name: "Environment", icon: "🌱", color: "#a8e6cf" },
    social: { name: "Social", icon: "👥", color: "#dda0dd" },
    military: { name: "Military", icon: "⚔️", color: "#ff6b6b" },
  }

  const generateLawEffectsWithAI = async (lawText, category) => {
    try {
      const response = await fetch("/api/generate-law-effects", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lawText: `${lawText.title}: ${lawText.description}`,
          category: category,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        return {
          effects: data.effects || [],
          resourceEffects: data.resourceEffects || {},
          colonyEffects: data.colonyEffects || {},
          citizenReaction: data.citizenReaction || "neutral",
        }
      }
    } catch (error) {
      console.log("AI law effects unavailable, using fallback")
    }

    // Fallback to manual generation
    return generateFallbackLawEffects(lawText, category)
  }

  const generateFallbackLawEffects = (law, category) => {
    const effects = {}
    const title = law.title.toLowerCase()
    const description = law.description.toLowerCase()

    // HARSH REALISTIC EFFECTS
    switch (category) {
      case "governance":
        if (title.includes("tax") || description.includes("tax")) {
          effects.materials = Math.floor(Math.random() * 150) + 100
          effects.colonyMorale = -(Math.floor(Math.random() * 25) + 20) // Heavy morale penalty
          effects.population = -(Math.floor(Math.random() * 80) + 40) // Some people leave/die
        }
        if (title.includes("freedom") || description.includes("liberty")) {
          effects.colonyMorale = Math.floor(Math.random() * 30) + 25
          effects.productivity = Math.floor(Math.random() * 20) + 15
        }
        if (title.includes("ration") || description.includes("limit")) {
          effects.food = -(Math.floor(Math.random() * 250) + 150) // Severe food reduction
          effects.water = -(Math.floor(Math.random() * 200) + 100) // Water reduction too
          effects.colonyMorale = -(Math.floor(Math.random() * 40) + 30) // People HATE rationing
        }
        break

      case "environment":
        if (
          title.includes("water") &&
          (title.includes("ban") || title.includes("no") || title.includes("stop") || title.includes("not allowed"))
        ) {
          // Water ban laws - DEADLY
          effects.water = -(Math.floor(Math.random() * 400) + 300) // Massive water loss
          effects.colonyHealth = -(Math.floor(Math.random() * 50) + 40) // Health crisis
          effects.colonyMorale = -(Math.floor(Math.random() * 60) + 50) // Extreme anger
          effects.population = -(Math.floor(Math.random() * 300) + 200) // People die/leave
        }
        break

      case "social":
        if (title.includes("work") && (title.includes("hour") || title.includes("time"))) {
          effects.materials = Math.floor(Math.random() * 120) + 80
          effects.colonyMorale = -(Math.floor(Math.random() * 30) + 20) // Longer work = unhappy
          effects.colonyHealth = -(Math.floor(Math.random() * 20) + 15) // Overwork = health issues
        }
        break

      case "military":
        if (title.includes("defense") || description.includes("security")) {
          effects.defenseRating = Math.floor(Math.random() * 40) + 30
          effects.materials = -(Math.floor(Math.random() * 200) + 150) // Defense is expensive
          effects.colonyMorale = -(Math.floor(Math.random() * 20) + 15) // Militarization scares people
        }
        break
    }

    return {
      effects: Object.keys(effects).map(
        (key) => `${key.replace(/([A-Z])/g, " $1").toLowerCase()}: ${effects[key] > 0 ? "+" : ""}${effects[key]}`,
      ),
      resourceEffects: effects,
      colonyEffects: {},
      citizenReaction: Object.values(effects).some((v) => v < 0) ? "angry" : "neutral",
    }
  }

  const handleCreateLaw = async () => {
    if (!newLaw.title || !newLaw.description) {
      addNotification({
        type: "error",
        title: "Incomplete Law",
        description: "Please fill in both title and description",
      })
      return
    }

    setIsCreating(true)

    try {
      // Generate AI-powered law effects
      const aiEffects = await generateLawEffectsWithAI(newLaw, selectedCategory)

      const law = {
        id: Date.now(),
        title: newLaw.title,
        description: newLaw.description,
        category: selectedCategory,
        effects: aiEffects.resourceEffects,
        predictedEffects: aiEffects.effects,
        citizenReaction: aiEffects.citizenReaction,
        enacted: false,
        createdAt: new Date().toISOString(),
      }

      setGameState((prev) => ({
        ...prev,
        laws: [...(prev.laws || []), law],
      }))

      addNotification({
        type: "success",
        title: "Law Created with AI Analysis",
        description: `Law "${newLaw.title}" created! Citizens are ${aiEffects.citizenReaction}!`,
      })

      setNewLaw({ title: "", description: "" })
    } catch (error) {
      addNotification({
        type: "error",
        title: "Creation Failed",
        description: "Failed to create law. Please try again.",
      })
    } finally {
      setIsCreating(false)
    }
  }

  const enactLaw = (lawId) => {
    const law = gameState.laws?.find((l) => l.id === lawId)
    if (!law || law.enacted) return

    // Apply law effects IMMEDIATELY and HARSHLY
    setGameState((prev) => {
      const newState = { ...prev }

      // Apply resource effects
      Object.entries(law.effects).forEach(([key, value]) => {
        if (newState.resources[key] !== undefined) {
          newState.resources[key] = Math.max(0, newState.resources[key] + value)
        } else if (key === "colonyHealth") {
          newState.colonyHealth = Math.min(100, Math.max(0, newState.colonyHealth + value))
        } else if (key === "colonyMorale") {
          newState.colonyMorale = Math.min(100, Math.max(0, newState.colonyMorale + value))
        } else if (key === "population") {
          newState.population = Math.max(0, newState.population + value)
        }
      })

      // Mark law as enacted
      newState.laws = newState.laws.map((l) => (l.id === lawId ? { ...l, enacted: true } : l))

      return newState
    })

    // Show detailed effects notification
    const effectsList = Object.entries(law.effects)
      .map(([key, value]) => `${key}: ${value > 0 ? "+" : ""}${value}`)
      .join(", ")

    addNotification({
      type: law.effects.population < 0 || law.effects.colonyHealth < -20 ? "error" : "success",
      title: "Law Enacted - Citizens React!",
      description: `"${law.title}" is now in effect! Effects: ${effectsList}`,
    })

    // Generate angry citizen reaction for bad laws
    setTimeout(() => {
      const negativeEffects = Object.entries(law.effects).filter(([key, value]) => value < 0)
      if (negativeEffects.length > 0 || law.citizenReaction === "angry") {
        addNotification({
          type: "warning",
          title: "📧 Angry Citizens!",
          description: "Citizens are furious about your new law! Check your email!",
        })
      }
    }, 3000)
  }

  const repealLaw = (lawId) => {
    const law = gameState.laws?.find((l) => l.id === lawId)
    if (!law) return

    // Reverse some effects when repealing (not all, some damage is permanent)
    if (law.enacted) {
      setGameState((prev) => {
        const newState = { ...prev }

        // Reverse 50% of the effects
        Object.entries(law.effects).forEach(([key, value]) => {
          const reverseValue = Math.floor(value * -0.5)
          if (newState.resources[key] !== undefined) {
            newState.resources[key] = Math.max(0, newState.resources[key] + reverseValue)
          } else if (key === "colonyHealth") {
            newState.colonyHealth = Math.min(100, Math.max(0, newState.colonyHealth + reverseValue))
          } else if (key === "colonyMorale") {
            newState.colonyMorale = Math.min(100, Math.max(0, newState.colonyMorale + reverseValue))
          }
        })

        return newState
      })
    }

    setGameState((prev) => ({
      ...prev,
      laws: prev.laws?.filter((l) => l.id !== lawId) || [],
    }))

    addNotification({
      type: "info",
      title: "Law Repealed",
      description: `"${law.title}" has been repealed. Some effects may be permanent.`,
    })
  }

  return (
    <div className="laws-system">
      <div className="laws-header">
        <div className="header-content">
          <h2 className="section-title">
            <span className="title-icon">⚖️</span>
            Colonial Law System
          </h2>
          <p className="section-subtitle">Create and manage laws that shape your colony's future</p>
          <div className="warning-notice">⚠️ Laws have REAL consequences - AI predicts citizen reactions!</div>
        </div>
      </div>

      <div className="laws-content">
        <div className="law-creation-panel">
          <h3 className="panel-title">
            <span className="panel-icon">📜</span>
            Create New Law (YOUR TEAM WILL ANALYZE)
          </h3>

          <div className="category-selector">
            {Object.entries(lawCategories).map(([categoryId, category]) => (
              <button
                key={categoryId}
                className={`category-btn ${selectedCategory === categoryId ? "active" : ""}`}
                onClick={() => setSelectedCategory(categoryId)}
                style={{
                  borderColor: selectedCategory === categoryId ? category.color : "rgba(59, 130, 246, 0.3)",
                  backgroundColor: selectedCategory === categoryId ? `${category.color}20` : "rgba(30, 41, 59, 0.8)",
                }}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
              </button>
            ))}
          </div>

          <div className="law-form">
            <div className="form-group">
              <label htmlFor="law-title">Law Title</label>
              <input
                id="law-title"
                type="text"
                className="law-input"
                value={newLaw.title}
                onChange={(e) => setNewLaw({ ...newLaw, title: e.target.value })}
                placeholder="e.g., Water Conservation Act"
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="law-description">Law Description</label>
              <textarea
                id="law-description"
                className="law-textarea"
                value={newLaw.description}
                onChange={(e) => setNewLaw({ ...newLaw, description: e.target.value })}
                placeholder="Describe the law's purpose and implementation..."
                rows={4}
                maxLength={500}
              />
            </div>

            <button className="create-law-btn" onClick={handleCreateLaw} disabled={isCreating}>
              {isCreating ? (
                <>
                  <div className="loading-spinner"></div>
                  Analyzing...
                </>
              ) : (
                "Create Law"
              )}
            </button>
          </div>
        </div>

        <div className="laws-list-panel">
          <h3 className="panel-title">
            <span className="panel-icon">⚖️</span>
            Active Laws ({gameState.laws?.length || 0})
          </h3>

          {!gameState.laws || gameState.laws.length === 0 ? (
            <div className="no-laws">
              <div className="no-laws-icon">📜</div>
              <p>No laws have been created yet.</p>
              <p className="no-laws-subtitle">Create your first law to begin governing your colony!</p>
            </div>
          ) : (
            <div className="laws-list">
              {gameState.laws.map((law) => (
                <div key={law.id} className={`law-card ${law.enacted ? "enacted" : ""}`}>
                  <div className="law-header">
                    <div className="law-info">
                      <div className="law-category">
                        <span
                          className="category-badge"
                          style={{ backgroundColor: lawCategories[law.category]?.color }}
                        >
                          {lawCategories[law.category]?.icon} {lawCategories[law.category]?.name}
                        </span>
                      </div>
                      <h4 className="law-title">{law.title}</h4>
                    </div>
                    <div className="law-status">
                      {law.enacted ? (
                        <span className="status-badge enacted">✅ Enacted</span>
                      ) : (
                        <span className="status-badge pending">⏳ Pending</span>
                      )}
                    </div>
                  </div>

                  <p className="law-description">{law.description}</p>

                  <div className="law-effects">
                    <h5>🤖 AI Predicted Effects:</h5>
                    <div className="effects-list">
                      {law.predictedEffects && law.predictedEffects.length > 0
                        ? law.predictedEffects.map((effect, index) => (
                            <div key={index} className="effect-item">
                              • {effect}
                            </div>
                          ))
                        : Object.entries(law.effects).map(([effect, value]) => (
                            <span key={effect} className={`effect-tag ${value > 0 ? "positive" : "negative"}`}>
                              {effect}: {value > 0 ? "+" : ""}
                              {value}
                            </span>
                          ))}
                    </div>
                    {law.citizenReaction && (
                      <div className={`citizen-reaction ${law.citizenReaction}`}>
                        Citizens are: <strong>{law.citizenReaction}</strong>
                      </div>
                    )}
                  </div>

                  <div className="law-actions">
                    {!law.enacted ? (
                      <button className="enact-btn" onClick={() => enactLaw(law.id)}>
                        ⚖️ Enact Law
                      </button>
                    ) : (
                      <button className="repeal-btn" onClick={() => repealLaw(law.id)}>
                        🗑️ Repeal Law
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LawsSystem
